import { useState, useEffect, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { getArticles, getCategories } from "../lib/api";
import ArticleCard from "../components/ArticleCard";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Calendar } from "../components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Skeleton } from "../components/ui/skeleton";
import { Search, CalendarIcon, MapPin, Filter, X } from "lucide-react";
import { format } from "date-fns";
import { fr } from "date-fns/locale";

export default function SearchPage() {
  const { t, i18n } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [articles, setArticles] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showFilters, setShowFilters] = useState(false);
  
  const [filters, setFilters] = useState({
    search: searchParams.get("q") || "",
    category: searchParams.get("category") || "",
    destination: searchParams.get("destination") || "",
    date: searchParams.get("date") ? new Date(searchParams.get("date")) : null,
  });

  const searchArticles = useCallback(async () => {
    setLoading(true);
    try {
      const params = { status: "published" };
      if (filters.search) params.search = filters.search;
      if (filters.category) params.category = filters.category;
      if (filters.destination) params.destination = filters.destination;
      // Phase E2 — locale active → résultats localisés (fallback FR côté back
      // si la traduction est réellement absente, aucun badge trompeur).
      if (i18n.language && i18n.language !== "fr") params.lng = i18n.language;

      const res = await getArticles(params);
      let results = res.data;
      
      // Client-side date filtering (could be improved server-side)
      if (filters.date) {
        const filterDateStr = format(filters.date, "yyyy-MM-dd");
        results = results.filter(article => 
          article.created_at.startsWith(filterDateStr)
        );
      }
      
      setArticles(results);
    } catch (error) {
      console.error("Error searching articles:", error);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    loadCategories();
  }, []);

  useEffect(() => {
    searchArticles();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, searchArticles]);

  const loadCategories = async () => {
    try {
      const res = await getCategories();
      setCategories(res.data);
    } catch (error) {
      console.error("Error loading categories:", error);
    }
  };

  const updateFilter = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    
    // Update URL params
    const newParams = new URLSearchParams(searchParams);
    if (value) {
      newParams.set(key === "search" ? "q" : key, value instanceof Date ? value.toISOString() : value);
    } else {
      newParams.delete(key === "search" ? "q" : key);
    }
    setSearchParams(newParams);
  };

  const clearFilters = () => {
    setFilters({ search: "", category: "", destination: "", date: null });
    setSearchParams({});
  };

  const hasActiveFilters = filters.search || filters.category || filters.destination || filters.date;

  return (
    <div className="min-h-screen bg-secondary pt-20" data-testid="search-page">
      {/* Header */}
      <section className="bg-white border-b border-border py-12 md:py-16">
        <div className="max-w-7xl mx-auto container-padding">
          <h1 className="font-heading text-3xl md:text-5xl font-bold text-primary mb-4">
            {t("search.title")}
          </h1>
          <p className="text-muted-foreground text-lg max-w-2xl">
            {t("search.subtitle")}
          </p>
        </div>
      </section>

      {/* Search & Filters */}
      <section className="sticky top-20 z-30 bg-secondary/95 backdrop-blur-sm border-b border-border py-4">
        <div className="max-w-7xl mx-auto container-padding">
          <div className="flex flex-col md:flex-row gap-4">
            {/* Search Input */}
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
              <Input
                placeholder={t("search.placeholder")}
                value={filters.search}
                onChange={(e) => updateFilter("search", e.target.value)}
                className="pl-10 h-12 bg-white"
                data-testid="search-input"
              />
            </div>

            {/* Filter Toggle (Mobile) */}
            <Button
              variant="outline"
              className="md:hidden flex items-center gap-2"
              onClick={() => setShowFilters(!showFilters)}
              data-testid="filter-toggle-btn"
            >
              <Filter className="h-4 w-4" />
              {t("search.filters")}
              {hasActiveFilters && <span className="w-2 h-2 bg-accent rounded-full" />}
            </Button>

            {/* Desktop Filters */}
            <div className="hidden md:flex items-center gap-3">
              {/* Category */}
              <Select value={filters.category || "all"} onValueChange={(v) => updateFilter("category", v === "all" ? "" : v)}>
                <SelectTrigger className="w-40 h-12 bg-white" data-testid="category-select">
                  <SelectValue placeholder={t("search.categoryPlaceholder")} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t("search.allCategories")}</SelectItem>
                  {categories.map(cat => (
                    <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              {/* Destination */}
              <div className="relative">
                <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder={t("search.destinationPlaceholder")}
                  value={filters.destination}
                  onChange={(e) => updateFilter("destination", e.target.value)}
                  className="pl-10 w-40 h-12 bg-white"
                  data-testid="destination-input"
                />
              </div>

              {/* Date */}
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="h-12 w-40 justify-start bg-white" data-testid="date-picker-trigger">
                    <CalendarIcon className="mr-2 h-4 w-4 text-muted-foreground" />
                    {filters.date ? format(filters.date, "d MMM", { locale: fr }) : t("search.date")}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="end">
                  <Calendar
                    mode="single"
                    selected={filters.date}
                    onSelect={(date) => updateFilter("date", date)}
                    locale={fr}
                  />
                </PopoverContent>
              </Popover>

              {/* Clear Filters */}
              {hasActiveFilters && (
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={clearFilters}
                  className="h-12 w-12"
                  data-testid="clear-filters-btn"
                >
                  <X className="h-4 w-4" />
                </Button>
              )}
            </div>
          </div>

          {/* Mobile Filters Panel */}
          {showFilters && (
            <div className="md:hidden mt-4 pt-4 border-t border-border space-y-4 animate-fade-in">
              <Select value={filters.category || "all"} onValueChange={(v) => updateFilter("category", v === "all" ? "" : v)}>
                <SelectTrigger className="w-full h-12 bg-white">
                  <SelectValue placeholder={t("search.categoryPlaceholder")} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t("search.allCategories")}</SelectItem>
                  {categories.map(cat => (
                    <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <div className="relative">
                <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder={t("search.destinationPlaceholder")}
                  value={filters.destination}
                  onChange={(e) => updateFilter("destination", e.target.value)}
                  className="pl-10 h-12 bg-white"
                />
              </div>

              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="w-full h-12 justify-start bg-white">
                    <CalendarIcon className="mr-2 h-4 w-4 text-muted-foreground" />
                    {filters.date ? format(filters.date, "d MMMM yyyy", { locale: fr }) : t("search.selectDate")}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0">
                  <Calendar
                    mode="single"
                    selected={filters.date}
                    onSelect={(date) => updateFilter("date", date)}
                    locale={fr}
                  />
                </PopoverContent>
              </Popover>

              {hasActiveFilters && (
                <Button variant="outline" onClick={clearFilters} className="w-full">
                  {t("search.clear")}
                </Button>
              )}
            </div>
          )}
        </div>
      </section>

      {/* Results */}
      <section className="section-padding">
        <div className="max-w-7xl mx-auto container-padding">
          <p className="text-muted-foreground mb-8">
            {loading ? t("search.loading") : `${articles.length} ${t("search.results")}`}
          </p>

          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {Array(6).fill(0).map((_, i) => (
                <div key={i}>
                  <Skeleton className="aspect-[4/3] mb-4" />
                  <Skeleton className="h-6 w-3/4 mb-2" />
                  <Skeleton className="h-4 w-full" />
                </div>
              ))}
            </div>
          ) : articles.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 md:gap-12">
              {articles.map((article, index) => (
                <div 
                  key={article.id} 
                  className="animate-slide-up"
                  style={{ animationDelay: `${index * 0.05}s` }}
                >
                  <ArticleCard article={article} />
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-16">
              <Search className="h-16 w-16 text-muted-foreground/30 mx-auto mb-4" />
              <h3 className="font-heading text-2xl font-semibold text-primary mb-2">
                {t("search.empty")}
              </h3>
              <p className="text-muted-foreground mb-6">
                {t("search.subtitle")}
              </p>
              {hasActiveFilters && (
                <Button variant="outline" onClick={clearFilters}>
                  {t("search.clear")}
                </Button>
              )}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
